import { useState, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Send, Trash2, Eye, ChevronDown, ChevronRight, CheckCircle, XCircle, Pencil, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminAction } from "@/lib/adminApi";
import { successToast, errorToast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";
import {
  AdminPageHeader,
  AdminPanel,
  AdminEmptyState,
  AdminInput,
  AdminPill,
  AdminPageSkeleton,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "./AdminUI";

const typeTone: Record<string, "info" | "warning" | "success"> = {
  info: "info",
  warning: "warning",
  success: "success",
};

function AnnouncementDetail({ announcementId }: { announcementId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "announcement_detail", announcementId],
    queryFn: () => adminAction("announcement_detail", { announcement_id: announcementId }),
  });

  if (isLoading) return <div className="py-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;

  const viewed = data?.viewed || [];
  const notViewed = data?.not_viewed || [];

  return (
    <div className="grid grid-cols-2 gap-8 px-8 py-6">
      <div>
        <div className="flex items-center gap-1.5 mb-3">
          <CheckCircle className="w-3.5 h-3.5 text-[#16a34a]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#16a34a]">Viewed · {viewed.length}</span>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {viewed.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">No views yet</p>
          ) : viewed.map((u: any) => (
            <div key={u.id} className="flex items-center justify-between text-[12px]">
              <div className="min-w-0 truncate">
                <span className="text-foreground">{u.name || "Unnamed"}</span>
                <span className="text-muted-foreground ml-2">{u.email}</span>
              </div>
              <span className="text-muted-foreground/60 text-[10.5px] tabular-nums">
                {new Date(u.viewed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-1.5 mb-3">
          <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Not viewed · {notViewed.length}</span>
        </div>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {notViewed.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">Everyone has viewed</p>
          ) : notViewed.map((u: any) => (
            <div key={u.id} className="text-[12px]">
              <span className="text-foreground">{u.name || "Unnamed"}</span>
              <span className="text-muted-foreground ml-2">{u.email}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AdminAnnouncementsTab() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState<string>("info");
  const [targetType, setTargetType] = useState<string>("all");
  const [targetOrgId, setTargetOrgId] = useState<string>("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const resetForm = () => {
    setTitle(""); setMessage(""); setType("info"); setTargetType("all"); setTargetOrgId(""); setEditingId(null);
  };

  const startEdit = (a: any) => {
    setTitle(a.title || ""); setMessage(a.message || ""); setType(a.type || "info");
    setTargetType(a.target_type || "all"); setTargetOrgId(a.target_org_id || "");
    setEditingId(a.id); setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const { data: announcements, isLoading } = useQuery({
    queryKey: ["admin", "announcements"],
    queryFn: () => adminAction("announcements_list"),
  });

  const { data: orgs } = useQuery({
    queryKey: ["admin", "organizations"],
    queryFn: () => adminAction("organizations"),
  });

  const create = useMutation({
    mutationFn: () => adminAction("announcements_create", {
      title, message, type,
      target_type: targetType,
      target_org_id: targetType === "org" ? targetOrgId : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "announcements"] });
      successToast("Sent", "Announcement published.");
      setShowForm(false); resetForm();
    },
    onError: (e: Error) => errorToast("Error", e.message),
  });

  const update = useMutation({
    mutationFn: () => adminAction("announcements_update", {
      announcement_id: editingId,
      title, message, type,
      target_type: targetType,
      target_org_id: targetType === "org" ? targetOrgId : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "announcements"] });
      successToast("Saved", "Announcement updated.");
      setShowForm(false); resetForm();
    },
    onError: (e: Error) => errorToast("Error", e.message),
  });

  const deleteAnnouncement = useMutation({
    mutationFn: (announcementId: string) => adminAction("announcements_delete", { announcement_id: announcementId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "announcements"] });
      successToast("Deleted", "Announcement removed.");
    },
    onError: (e: Error) => errorToast("Error", e.message),
  });

  const handlePublish = () => editingId ? update.mutate() : create.mutate();

  if (isLoading) return <AdminPageSkeleton stats={0} rows={6} columns={6} />;

  const typeOptions = ["info", "warning", "success"] as const;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Communications"
        title="Announcements"
        description="Send platform-wide or per-organisation messages to your users."
        meta={`${announcements?.length || 0} ${(announcements?.length ?? 0) === 1 ? "announcement" : "announcements"}`}
        actions={
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New
          </Button>
        }
      />

      {showForm && (
        <AdminPanel padded className="space-y-4">
          <AdminInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Announcement title…"
          />
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Announcement message…"
            rows={3}
            className="w-full rounded-[3px] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-[#c2541a] focus:ring-1 focus:ring-[rgba(194,84,26,0.25)] resize-none"
            style={{ background: "#faf7f1", border: "1px solid rgba(58, 53, 48, 0.14)" }}
          />
          <div className="flex flex-wrap gap-3 items-center">
            <div className="inline-flex rounded-[3px] overflow-hidden" style={{ border: "1px solid rgba(58,53,48,0.12)" }}>
              {typeOptions.map((t) => (
                <button
                  key={t}
                  onClick={() => setType(t)}
                  className={cn(
                    "px-3 py-1.5 text-[10.5px] uppercase tracking-[0.18em] transition-colors",
                    type === t ? "text-white" : "text-muted-foreground hover:text-foreground"
                  )}
                  style={type === t ? { background: "#c2541a" } : undefined}
                >
                  {t}
                </button>
              ))}
            </div>
            <select
              value={targetType}
              onChange={(e) => setTargetType(e.target.value)}
              className="h-8 rounded-[3px] px-2 text-[12px] text-foreground"
              style={{ background: "#faf7f1", border: "1px solid rgba(58,53,48,0.14)" }}
            >
              <option value="all">All users</option>
              <option value="org">Specific org</option>
            </select>
            {targetType === "org" && (
              <select
                value={targetOrgId}
                onChange={(e) => setTargetOrgId(e.target.value)}
                className="h-8 rounded-[3px] px-2 text-[12px] text-foreground"
                style={{ background: "#faf7f1", border: "1px solid rgba(58,53,48,0.14)" }}
              >
                <option value="">Select org…</option>
                {(orgs || []).map((o: any) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={handlePublish} disabled={!title.trim() || !message.trim() || create.isPending || update.isPending || (targetType === "org" && !targetOrgId)} size="sm">
              {(create.isPending || update.isPending) ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              {editingId ? "Save changes" : "Publish"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); resetForm(); }}>Cancel</Button>
          </div>
        </AdminPanel>
      )}

      {(!announcements || announcements.length === 0) ? (
        <AdminEmptyState icon={Megaphone} title="No announcements" description="Publish your first message to reach customers." />
      ) : (
        <AdminPanel>
          <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
              <tr>
                <th className="w-10" />
                <th className={adminTableHeadCellClass}>Title</th>
                <th className={adminTableHeadCellClass}>Type</th>
                <th className={adminTableHeadCellClass}>Views</th>
                <th className={adminTableHeadCellClass}>Target</th>
                <th className={adminTableHeadCellClass}>Date</th>
                <th className="w-20" />
              </tr>
            </thead>
            <tbody>
              {(announcements || []).map((a: any, i: number) => {
                const isExpanded = expandedId === a.id;
                return (
                  <Fragment key={a.id}>
                    <tr
                      className={"cursor-pointer " + adminTableRowClass}
                      style={{ borderTop: i === 0 ? "none" : "1px solid rgba(58,53,48,0.06)" }}
                      onClick={() => setExpandedId(isExpanded ? null : a.id)}
                    >
                      <td className="px-4 py-3.5">
                        {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                      </td>
                      <td className={adminTableCellClass + " text-foreground"} style={{ fontWeight: 500 }}>{a.title}</td>
                      <td className={adminTableCellClass}><AdminPill tone={typeTone[a.type] || "neutral"}>{a.type}</AdminPill></td>
                      <td className={adminTableCellClass + " text-muted-foreground"}>
                        <div className="flex items-center gap-1.5 tabular-nums">
                          <Eye className="w-3.5 h-3.5" />
                          <span>{a.view_count} / {a.total_users}</span>
                        </div>
                      </td>
                      <td className={adminTableCellClass + " text-muted-foreground"}>
                        {a.target_type === "all" ? "All users" : a.organizations?.name || "Specific org"}
                      </td>
                      <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                        {new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-[#c2541a]" onClick={(e) => { e.stopPropagation(); startEdit(a); }}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" onClick={(e) => e.stopPropagation()}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete announcement</AlertDialogTitle>
                                <AlertDialogDescription>This will permanently remove "{a.title}" for all users.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteAnnouncement.mutate(a.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} style={{ background: "rgba(58,53,48,0.025)", borderTop: "1px solid rgba(58,53,48,0.06)" }}>
                          <div className="px-8 py-5" style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
                            <p className="text-[13px] text-foreground leading-relaxed">{a.message}</p>
                          </div>
                          <AnnouncementDetail announcementId={a.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </AdminPanel>
      )}
    </div>
  );
}
